import { RosterLevelEnum } from '@wwe-portal/entities/roster.type';
import {
  GemPerLevel,
  SuperstarStats,
  SuperstarStatsSheet,
} from '@wwe-portal/entities/superstar-stats.type';
import { STATE_ADDITION, STATE_MULTIPLIER } from './constants';
import { levelEnumToLabel } from '@wwe-portal/utils/roster/level';

export const calculateSuperstarStats = (
  statsSheet: SuperstarStatsSheet,
  {
    currentStar,
    currentStarColor,
  }: {
    currentStar: string;
    currentStarColor: RosterLevelEnum | undefined;
  }
) => {
  if (!currentStarColor) return undefined;
  return (
    statsSheet[levelEnumToLabel[currentStarColor]][currentStar] ?? undefined
  );
};

export const findGemStats = (
  superstarStats: SuperstarStats | undefined,
  currentLevel: number
) => {
  if (!superstarStats) return undefined;

  return (
    superstarStats.Stats.find((item) => item.level === currentLevel) ??
    undefined
  );
};

export const calculateGemStats = (
  statsSheet: SuperstarStatsSheet,
  {
    currentStar,
    currentStarColor,
    currentLevel,
  }: {
    currentStar: string;
    currentStarColor: RosterLevelEnum | undefined;
    currentLevel: number;
  }
) => {
  const superstarStats = calculateSuperstarStats(statsSheet, {
    currentStar,
    currentStarColor,
  });

  if (!superstarStats) return undefined;

  const stats =
    superstarStats.Stats.find((item) => item.level === currentLevel) ?? null;
  return stats;
};

export const parseLevelOptionValue = ({
  solidStars,
  hollowStars,
}: {
  solidStars: number;
  hollowStars: number;
}) => {
  return `${solidStars + hollowStars}_${solidStars}`;
};

export const isSuperstarLevel = (
  level: string | undefined
): level is RosterLevelEnum => {
  return level === 'B' || level === 'S' || level === 'G';
};

export const parseSuperstarLevel = (level: string | undefined) => {
  if (!isSuperstarLevel(level)) return undefined;
  return levelEnumToLabel[level];
};

export const parseSuperstarStarCode = (star: string | undefined) => {
  if (!star) return undefined;
  const [totalStars, solidStars] = star.split('_');
  if (!totalStars || !solidStars) return undefined;
  const totalStarNumber = parseInt(totalStars);
  const solidStarsNumber = parseInt(solidStars);
  return {
    solidStars: solidStarsNumber,
    hollowStars: totalStarNumber - solidStarsNumber,
  };
};

export const getTotalHP = (gemStats: GemPerLevel | undefined) => {
  return gemStats?.stats.HP ?? undefined;
};

export const calculateAbilityScore = (
  superstarStats: SuperstarStats | undefined,
  selectedAbilities: {
    localId: string | undefined;
    equipped: boolean;
    level: number | undefined;
    gemType: string | undefined;
  }[]
) => {
  if (!superstarStats) return null;
  const abilities = superstarStats.Abilities;
  let totalAbilityScore = 0;
  for (const ability of selectedAbilities) {
    if (ability.level == null) continue;
    if (!ability.equipped) continue;
    const abilityStats = abilities.find((item) => item.ID === ability.localId);
    const abilityScore = abilityStats?.levels[ability.level - 1]?.talentScore;
    if (abilityScore != null) {
      totalAbilityScore += abilityScore;
    }
  }
  return totalAbilityScore;
};

export const calculateTotalTalent = (
  superstarStats: SuperstarStats | undefined,
  currentLevel: number,
  selectedAbilities: {
    localId: string | undefined;
    equipped: boolean;
    level: number | undefined;
    gemType: string | undefined;
  }[]
) => {
  const talentScore = findGemStats(superstarStats, currentLevel)?.stats
    .TalentScore;
  const abilityScore = calculateAbilityScore(superstarStats, selectedAbilities);
  if (talentScore == null || abilityScore == null) return undefined;
  return Math.ceil(
    (talentScore + abilityScore + STATE_ADDITION) / STATE_MULTIPLIER
  );
};
